import React, {useState, useEffect, useCallback} from 'react';
import {Button, Dialog, DialogBody, Icon, MenuItem, Spinner, Tag, Tooltip} from '@blueprintjs/core';
import {Select} from '@blueprintjs/select';

import {cameraServers, recordings} from '/src/services/api';
import {device} from '/src/services/iot-api';
import {errorToast, successToast} from '/src/utils/toaster';

const STEP_CHOICE = 'choice';
const STEP_REPLACE = 'replace';
const STEP_DECOMMISSION = 'decommission';

const REPLACE_SELECT = 'select';
const REPLACE_DETACH_TARGET = 'detach-target';
const REPLACE_RELOCATE_TARGET = 'relocate-target';
const REPLACE_MOVE_RECORDINGS = 'move-recordings';
const REPLACE_DECOMMISSION_OLD = 'decommission-old';
const REPLACE_DONE = 'done';

const AURA_ORG_ID = '5ab28c7917186b002ca9942d';
const AURA_LOCATION_ID = '5dfb59e00c7bbe00282e3124';

const ReplaceStep = ({deviceId, deviceName, organisationId, locationId, locationName, onClose}) => {
    const [devices, setDevices] = useState(null);
    const [thisDeviceRecs, setThisDeviceRecs] = useState(null);
    const [selected, setSelected] = useState(null);
    const [subStep, setSubStep] = useState(REPLACE_SELECT);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        cameraServers.list({
            withinOrganisations: organisationId,
            include: 'recordings',
        }).then(devs => {
            const thisDevice = devs.find(d => d.id === deviceId);
            setThisDeviceRecs(thisDevice?.recordings ?? []);
            setDevices(devs.filter(d => d.id !== deviceId && d.location === locationId));
        }).catch(() => {
            errorToast({message: 'Failed to load devices'});
        });
    }, []);

    const selectedRecs = selected?.recordings ?? [];

    const handleDetachTarget = useCallback(() => {
        if (!selected) return;
        setLoading(true);

        Promise.allSettled(selectedRecs.map(rec =>
            recordings.update(rec.id, {cameraServer: null})
        )).then(results => {
            const failed = results.filter(r => r.status === 'rejected');
            if (failed.length) {
                errorToast({message: `Failed to detach ${failed.length} of ${selectedRecs.length} recordings`});
            } else {
                successToast({message: `Detached ${selectedRecs.length} recording${selectedRecs.length !== 1 ? 's' : ''} from ${selected.name}`});
                setSubStep(REPLACE_RELOCATE_TARGET);
            }
        }).finally(() => setLoading(false));
    }, [selected, selectedRecs]);

    const handleRelocateTarget = useCallback(() => {
        if (!selected || !locationId) return;
        setLoading(true);

        cameraServers.update(selected.id, {location: locationId, status: 'connection confirmed'})
            .then(() => {
                successToast({message: `Moved ${selected.name} to ${deviceName}'s location`});
                setSubStep(REPLACE_MOVE_RECORDINGS);
            })
            .catch(err => {
                errorToast({message: err?.message ?? 'Failed to move device'});
            })
            .finally(() => setLoading(false));
    }, [selected, locationId, deviceName]);

    const handleMoveRecordings = useCallback(() => {
        if (!selected || !thisDeviceRecs?.length) return;
        setLoading(true);

        Promise.allSettled(thisDeviceRecs.map(rec =>
            recordings.update(rec.id, {cameraServer: selected.id})
        )).then(results => {
            const failed = results.filter(r => r.status === 'rejected');
            if (failed.length) {
                errorToast({message: `Failed to move ${failed.length} of ${thisDeviceRecs.length} recordings`});
            } else {
                successToast({message: `Moved ${thisDeviceRecs.length} recording${thisDeviceRecs.length !== 1 ? 's' : ''} to ${selected.name}`});
                device.command('UpdateHandledRecids', deviceName, null, 'update').catch(() => {});
                device.command('UpdateHandledRecids', selected.name, null, 'update').catch(() => {});
                setSubStep(REPLACE_DECOMMISSION_OLD);
            }
        }).finally(() => setLoading(false));
    }, [selected, thisDeviceRecs, deviceName]);

    const handleDecommissionOld = useCallback((moveToAura) => {
        setLoading(true);
        const update = {status: 'decommissioned'};
        if (moveToAura) {
            update.organisation = AURA_ORG_ID;
            update.location = AURA_LOCATION_ID;
        }
        cameraServers.update(deviceId, update)
            .then(() => {
                successToast({message: `${deviceName} has been decommissioned`});
                // Fire-and-forget: notify both devices to update their config
                device.command('UpdateHandledRecids', deviceName, null, 'update').catch(() => {});
                device.command('UpdateDeviceConfiguration', deviceName, null, 'update').catch(() => {});
                device.command('UpdateHandledRecids', selected.name, null, 'update').catch(() => {});
                device.command('UpdateDeviceConfiguration', selected.name, null, 'update').catch(() => {});
                successToast({message: 'Sent device update commands'});
                setSubStep(REPLACE_DONE);
            })
            .catch(err => {
                errorToast({message: err?.message ?? 'Failed to decommission device'});
            })
            .finally(() => setLoading(false));
    }, [deviceId, deviceName, selected]);

    if (devices == null) return <Spinner size={24} />;

    if (!devices.length) return (
        <Tag minimal intent="warning" icon="warning-sign">No other devices in this organisation and location</Tag>
    );

    if (subStep === REPLACE_SELECT) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <Tooltip position="right" content={<div style={{maxWidth: 220}}>The target device must belong to the same organisation and be in <strong>{locationName}</strong>.</div>}>
                        <Icon icon="info-sign" style={{marginRight: 6, cursor: 'help'}} />
                    </Tooltip>
                    Select device replacing <strong>{deviceName}</strong>:
                </p>

                <Select
                    items={devices}
                    itemPredicate={(query, dev) => dev.name.toLowerCase().includes(query.toLowerCase())}
                    itemRenderer={(dev, {handleClick, modifiers}) => (
                        <MenuItem
                            key={dev.id}
                            text={dev.name}
                            label={`${dev.recordings?.length ?? 0} recs`}
                            onClick={handleClick}
                            active={modifiers.active}
                            selected={selected?.id === dev.id}
                        />
                    )}
                    onItemSelect={setSelected}
                    popoverProps={{minimal: true, matchTargetWidth: true}}
                    fill
                >
                    <Button
                        fill
                        text={selected ? selected.name : 'Select a device...'}
                        endIcon="caret-down"
                        icon={selected ? 'desktop' : undefined}
                    />
                </Select>

                {selected && (
                    <div style={{fontSize: 13, color: '#5c7080'}}>
                        <strong>{selected.name}</strong> currently has {selectedRecs.length} recording{selectedRecs.length !== 1 ? 's' : ''}
                        {selectedRecs.length > 0 && (
                            <div style={{color: '#c23030', marginTop: '0.75em', textAlign: 'center'}}>
                                <Icon icon="warning-sign" size={12} style={{marginRight: 4}} />
                                The recording{selectedRecs.length !== 1 ? 's' : ''} in <strong>{selected.name}</strong> would be orphaned.
                                <br />Decommission <strong>{selected.name}</strong> first to prevent this.
                            </div>
                        )}
                    </div>
                )}

                <Button
                    fill
                    intent="primary"
                    icon="swap-horizontal"
                    text={selected ? `Replace with ${selected.name}` : 'Replace'}
                    disabled={!selected}
                    onClick={() => setSubStep(selectedRecs.length > 0 ? REPLACE_DETACH_TARGET : REPLACE_RELOCATE_TARGET)}
                />
            </div>
        );
    }

    if (subStep === REPLACE_DETACH_TARGET) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <strong>Step 1:</strong> Detach {selectedRecs.length} recording{selectedRecs.length !== 1 ? 's' : ''} from <strong>{selected.name}</strong>
                </p>
                <p style={{margin: 0, fontSize: 13, color: '#5c7080'}}>
                    {selectedRecs.length === 1 ? 'This recording' : 'These recordings'} will be set to have no device. You can reassign {selectedRecs.length === 1 ? 'it' : 'them'} later.
                </p>
                <div style={{fontSize: 13, color: '#5c7080'}}>
                    {selectedRecs.map(rec => (
                        <div key={rec.id}>• {rec.id}</div>
                    ))}
                </div>
                <Button
                    fill
                    intent="warning"
                    icon="remove"
                    text={loading ? 'Detaching...' : `Detach ${selectedRecs.length} recording${selectedRecs.length !== 1 ? 's' : ''}`}
                    disabled={loading}
                    onClick={handleDetachTarget}
                />
            </div>
        );
    }

    if (subStep === REPLACE_RELOCATE_TARGET) {
        const stepNum = selectedRecs.length > 0 ? 2 : 1;
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <strong>Step {stepNum}:</strong> Move <strong>{selected.name}</strong> to <strong>{locationName || 'unknown location'}</strong>
                </p>
                <p style={{margin: 0, fontSize: 13, color: '#5c7080'}}>
                    This will update <strong>{selected.name}</strong>'s location to match <strong>{deviceName}</strong>'s ({locationName || 'unknown'}).
                </p>
                <Button
                    fill
                    intent="primary"
                    icon="map-marker"
                    text={loading ? 'Moving...' : `Move ${selected.name}`}
                    disabled={loading}
                    onClick={handleRelocateTarget}
                />
            </div>
        );
    }

    if (subStep === REPLACE_MOVE_RECORDINGS) {
        const stepNum = (selectedRecs.length > 0 ? 2 : 1) + 1;
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <strong>Step {stepNum}:</strong> Move recordings from <strong>{deviceName}</strong> to <strong>{selected.name}</strong>
                </p>
                {thisDeviceRecs?.length > 0 ? (
                    <>
                        <div style={{fontSize: 13, color: '#5c7080'}}>
                            {thisDeviceRecs.map(rec => (
                                <div key={rec.id}>• {rec.id}</div>
                            ))}
                        </div>
                        <Button
                            fill
                            intent="primary"
                            icon="swap-horizontal"
                            text={loading ? 'Moving...' : `Move ${thisDeviceRecs.length} recording${thisDeviceRecs.length !== 1 ? 's' : ''}`}
                            disabled={loading}
                            onClick={handleMoveRecordings}
                        />
                    </>
                ) : (
                    <>
                        <p style={{margin: 0, fontSize: 13, color: '#5c7080'}}>
                            No recordings to move.
                        </p>
                        <Button
                            fill
                            intent="primary"
                            text="Continue"
                            onClick={() => setSubStep(REPLACE_DECOMMISSION_OLD)}
                        />
                    </>
                )}
            </div>
        );
    }

    if (subStep === REPLACE_DECOMMISSION_OLD) {
        const stepNum = (selectedRecs.length > 0 ? 2 : 1) + 2;
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <strong>Step {stepNum}:</strong> Decommission <strong>{deviceName}</strong>
                </p>
                <p style={{margin: 0, fontSize: 13, color: '#5c7080'}}>
                    Move <strong>{deviceName}</strong> to the Aura organisation, or just mark it as decommissioned?
                </p>
                <div style={{display: 'flex', gap: '0.75em'}}>
                    <Button
                        fill
                        intent="warning"
                        icon="office"
                        text={loading ? 'Processing...' : 'Move to Aura & decommission'}
                        disabled={loading}
                        onClick={() => handleDecommissionOld(true)}
                    />
                    <Button
                        fill
                        intent="danger"
                        icon="remove"
                        text={loading ? 'Processing...' : 'Just decommission'}
                        disabled={loading}
                        onClick={() => handleDecommissionOld(false)}
                    />
                </div>
            </div>
        );
    }

    if (subStep === REPLACE_DONE) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em', textAlign: 'center'}}>
                <Icon icon="tick-circle" size={32} intent="success" />
                <p style={{margin: 0}}>
                    <strong>{selected.name}</strong> has replaced <strong>{deviceName}</strong>.
                </p>
                <Button fill text="Done" onClick={() => window.location.reload()} />
            </div>
        );
    }

    return null;
};

const DECOM_DETACH = 'detach';
const DECOM_SET_STATUS = 'set-status';
const DECOM_DONE = 'done';

const DecommissionStep = ({deviceId, deviceName, organisationId}) => {
    const [deviceRecs, setDeviceRecs] = useState(null);
    const [subStep, setSubStep] = useState(DECOM_DETACH);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        recordings.list({withinCameraServers: deviceId}).then(recs => {
            setDeviceRecs(recs);
        }).catch(() => {
            errorToast({message: 'Failed to load recordings'});
            setDeviceRecs([]);
        });
    }, []);

    const handleDetach = useCallback(() => {
        if (!deviceRecs?.length) return;
        setLoading(true);

        Promise.allSettled(deviceRecs.map(rec =>
            recordings.update(rec.id, {cameraServer: null})
        )).then(results => {
            const failed = results.filter(r => r.status === 'rejected');
            if (failed.length) {
                errorToast({message: `Failed to detach ${failed.length} of ${deviceRecs.length} recordings`});
            } else {
                successToast({message: `Detached ${deviceRecs.length} recording${deviceRecs.length !== 1 ? 's' : ''} from ${deviceName}`});
                device.command('UpdateHandledRecids', deviceName, null, 'update').catch(() => {});
                setSubStep(DECOM_SET_STATUS);
            }
        }).finally(() => setLoading(false));
    }, [deviceRecs, deviceName]);

    const handleDecommission = useCallback((moveToAura) => {
        setLoading(true);
        const update = {status: 'decommissioned'};
        if (moveToAura) {
            update.organisation = AURA_ORG_ID;
            update.location = AURA_LOCATION_ID;
        }
        cameraServers.update(deviceId, update)
            .then(() => {
                successToast({message: `${deviceName} has been decommissioned`});
                // Fire-and-forget: notify device to update its config
                device.command('UpdateHandledRecids', deviceName, null, 'update').catch(() => {});
                device.command('UpdateDeviceConfiguration', deviceName, null, 'update').catch(() => {});
                successToast({message: 'Sent device update commands'});
                setSubStep(DECOM_DONE);
            })
            .catch(err => {
                errorToast({message: err?.message ?? 'Failed to decommission device'});
            })
            .finally(() => setLoading(false));
    }, [deviceId, deviceName]);

    if (deviceRecs == null) return <Spinner size={24} />;

    if (subStep === DECOM_DETACH) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <strong>Step 1:</strong> Detach recordings from <strong>{deviceName}</strong>
                </p>
                {deviceRecs.length > 0 ? (
                    <>
                        <div style={{fontSize: 13, color: '#5c7080'}}>
                            {deviceRecs.map(rec => (
                                <div key={rec.id}>• {rec.id}</div>
                            ))}
                        </div>
                        <Button
                            fill
                            intent="warning"
                            icon="remove"
                            text={loading ? 'Detaching...' : `Detach ${deviceRecs.length} recording${deviceRecs.length !== 1 ? 's' : ''}`}
                            disabled={loading}
                            onClick={handleDetach}
                        />
                    </>
                ) : (
                    <>
                        <p style={{margin: 0, fontSize: 13, color: '#5c7080'}}>
                            No recordings to detach.
                        </p>
                        <Button
                            fill
                            intent="primary"
                            text="Continue"
                            onClick={() => setSubStep(DECOM_SET_STATUS)}
                        />
                    </>
                )}
            </div>
        );
    }

    if (subStep === DECOM_SET_STATUS) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                <p style={{margin: 0}}>
                    <strong>Step 2:</strong> Decommission <strong>{deviceName}</strong>
                </p>
                <p style={{margin: 0, fontSize: 13, color: '#5c7080'}}>
                    Move <strong>{deviceName}</strong> to the Aura organisation, or just mark it as decommissioned?
                </p>
                <div style={{display: 'flex', gap: '0.75em'}}>
                    <Button
                        fill
                        intent="warning"
                        icon="office"
                        text={loading ? 'Processing...' : 'Move to Aura & decommission'}
                        disabled={loading}
                        onClick={() => handleDecommission(true)}
                    />
                    <Button
                        fill
                        intent="danger"
                        icon="remove"
                        text={loading ? 'Processing...' : 'Just decommission'}
                        disabled={loading}
                        onClick={() => handleDecommission(false)}
                    />
                </div>
            </div>
        );
    }

    if (subStep === DECOM_DONE) {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em', textAlign: 'center'}}>
                <Icon icon="tick-circle" size={32} intent="success" />
                <p style={{margin: 0}}>
                    <strong>{deviceName}</strong> has been decommissioned.
                </p>
                <Button fill text="Done" onClick={() => window.location.reload()} />
            </div>
        );
    }

    return null;
};

const DecommissionDialog = ({deviceName, deviceId, organisationId, locationId, locationName}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState(STEP_CHOICE);

    const handleOpen = useCallback(() => {
        setStep(STEP_CHOICE);
        setIsOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const title = step === STEP_CHOICE
        ? 'Decommission device'
        : step === STEP_REPLACE
            ? 'Replace device'
            : 'Decommission device';

    return (
        <>
            <Button
                fill
                text="Decommission device"
                intent="danger"
                icon="remove"
                onClick={handleOpen}
            />
            <Dialog
                isOpen={isOpen}
                onClose={handleClose}
                title={title}
                icon={step === STEP_REPLACE ? 'swap-horizontal' : 'remove'}
            >
                <DialogBody>
                    {step === STEP_CHOICE && (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.75em'}}>
                            <p style={{margin: 0}}>
                                Replace this device, or just decommission?
                            </p>
                            <div style={{display: 'flex', gap: '0.75em'}}>
                                <Button
                                    fill
                                    icon="swap-horizontal"
                                    text="Replace"
                                    intent="primary"
                                    onClick={() => setStep(STEP_REPLACE)}
                                />
                                <Button
                                    fill
                                    icon="remove"
                                    text="Decommission"
                                    intent="danger"
                                    onClick={() => setStep(STEP_DECOMMISSION)}
                                />
                            </div>
                        </div>
                    )}
                    {step === STEP_REPLACE && (
                        <ReplaceStep
                            deviceId={deviceId}
                            deviceName={deviceName}
                            organisationId={organisationId}
                            locationId={locationId}
                            locationName={locationName}
                            onClose={handleClose}
                        />
                    )}
                    {step === STEP_DECOMMISSION && (
                        <DecommissionStep
                            deviceId={deviceId}
                            deviceName={deviceName}
                            organisationId={organisationId}
                        />
                    )}
                </DialogBody>
            </Dialog>
        </>
    );
};

export {DecommissionDialog};
